<template>
    <AppHead title="Neue Veranstaltung eingeben" />
    <div class="lg:px-64">
        <PageTitle><span v-if="date_id">Veranstaltung bearbeiten</span><span v-else>Neue Veranstaltung eingeben</span> - Schritt 3 von 3</PageTitle>
        <form @submit.prevent="submit">
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="category" value="Rubrik" />
                    <FormInput type="text" :value="category" class="mt-1 block w-full text-gray-500" readonly />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Der Name der Rubrik dient zur Orientierung und kann hier nicht geändert werden.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="location" value="Ort" />
                    <FormInput type="text" :value="location" class="mt-1 block w-full text-gray-500" readonly />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Der Name des Ortes dient zur Orientierung und kann hier nicht geändert werden.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="name" value="Name" />
                    <FormInput type="text" :value="name" class="mt-1 block w-full text-gray-500" readonly />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Der Name der Veranstaltung dient zur Orientierung und kann hier nicht geändert werden.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="date" value="Datum" />
                    <FormInput id="date" type="date" :value="form.date" v-model="form.date" class="mt-1 block w-full" required />
                    <FormError v-if="form.hasErrors && form.errors.date" :message="form.errors.date" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Nutzen Sie zur Datumseingabe das Kalendersymbol.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="time" value="Zeit" />
                    <FormInput id="time" type="time" :value="form.time" v-model="form.time" class="mt-1 block w-full" :required="form.finish ? false : true" />
                    <FormError v-if="form.hasErrors && form.errors.time" :message="form.errors.time" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Tragen Sie die Uhrzeit ein.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="finish" value="Ende" />
                    <FormInput id="finish" type="date" :value="form.finish" v-model="form.finish" class="mt-1 block w-full" :required="!form.time" :disabled="!parent" />
                    <FormError v-if="form.hasErrors && form.errors.finish" :message="form.errors.finish" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    End-Datum nur Rubriken Ausstellungen und Festivals
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="tags" value="Tags" />
                    <FormSelect id="tags" :required="false" multiple :options="options" :value="form.tags" class="mt-1 block w-full" v-model="form.tags" :reduce="tag => tag.value" />
                    <FormError v-if="form.hasErrors && form.errors.tags" :message="form.errors.tags" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Ein Tag ist eine Markierung der Veranstaltung, damit wir dieser zusätzliche Eigenschaften zuordnen können, z.B. ob Ihre Veranstaltung geeignet für
                    Kinder/Familien ist, Eintritt frei ist oder weihnachtlich ist.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2 flex items-center justify-end space-x-4 mt-4">
                    <Link as="button" class="btn-primary" :href="route('manager.media.create', [event, date_id])">
                        Zurück zu Schritt 2
                    </Link>
                    <FormButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Veröffentlichen und Veranstaltung anzeigen
                    </FormButton>
                </div>
                <div class="w-full lg:w-1/2 text-sm pt-5">
                    Mit Klick auf den Button wird Ihre Veranstaltung im Kulturkalender Dresden veröffentlicht.
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { Link, useForm } from "@inertiajs/vue3";
import AppHead from '@/Shared/Head';
import PageTitle from '@/Shared/Page/Title';
import FormError from '@/Shared/Form/Error';
import FormLabel from '@/Shared/Form/Label';
import FormInput from '@/Shared/Form/Input';
import FormSelect from '@/Shared/Form/Select';
import FormButton from '@/Shared/Form/Button';

const props = defineProps({
    event: String,
    category: String,
    parent: Boolean,
    location: String,
    name: String,
    date_id: Number,
    date: String,
    time: String,
    finish: String,
    tags: Array,
    options: Object,
})
const form = useForm({
    date: props.date,
    time: props.time,
    finish: props.finish,
    tags: props.tags,
});
const submit = () => {
    form.post(route('manager.date.store', [props.event, props.date_id]), {
        preserveScroll: true,
        onSuccess: (page) => {
            window.open(route('event', [props.event]), '_blank', 'popup,noopener');
        }
    });
};
</script>
